import React, { Component } from 'react';
import { CHAT_UNREAD_MESSAGE_REDIRECT } from '../../components/Chat/events';
import {
  ACCOUNT_HOME_PATH,
  CHECK_IN_TYPE_CONTACTLESS_BR,
  CHECK_IN_TYPE_CONTACTLESS_QR,
  PAPERWORK_TEXT_UPON_CONFIRMED,
  PAPERWORK_TEXT_UPON_RESERVED,
  TRACKING_CLICK_LINK_FOLLOW_UP_APPOINTMENT_TEXT,
  TRACKING_PAYMENT_VIEW_INVOICE,
  TRANSACTIONAL,
  WRAPUP,
} from '../../constants/index';
import {
  getDapiGoogleReviewEmailUrl,
  getDapiGoogleReviewUrl,
  getTrackingUrl,
  trackReviewLink,
} from '../../core/dapi/tracking';
import { getBookingById, getPublicBookingById } from '../../core/dapi/newBooking';
import { apiGetJson, apiPostJson } from '../../core/dapi';
import { isEmptyString } from '../../core/util/string';
import history from '../../core/history';
import {
  BOOK_VISIT_BODY_WRAPUP,
  BOOK_VISIT_FOOTER_TRANSACTIONAL,
  BOOK_VISIT_FOOTER_WRAPUP,
  BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_LOGIN,
  BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_LOGIN_ERROR,
  BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_PROFILE_PAGE,
  BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_WAITLIST,
  BOOKING_CONFIRMATION_WALK_IN_EMAIL_REDIRECT_TO_PROFILE_PAGE,
  BOOKING_CONFIRMATION_WALK_IN_EMAIL_REDIRECT_TO_WAITLIST,
  DOWNLOAD_DRIVER_CLICK_LINK,
  EMAIL_PAYMENTS_REDIRECT_TO_INVOICE,
  MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_BROWSER,
  MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_QR_CODE,
  MOBILE_CHECK_IN_REDIRECT_TO_WAITLIST,
  POST_VISIT_SUMMARY_CLICK_NOTIFICATION,
  REDIRECT_TO_ADD_CREDIT_CARD_SHORT_LINK,
  REDIRECT_TO_BOOKING_WIDGET,
  REDIRECT_TO_BOOKING_WIDGET_ERROR,
  REDIRECT_TO_CDP_ERROR,
  REDIRECT_TO_CONFIRMATION_SHORTLINK_ERROR,
  REDIRECT_TO_PAPERWORK,
  REDIRECT_TO_PVS_ERROR,
  REDIRECT_TO_REMINDER,
  REDIRECT_TO_REVIEW_RESPONSE,
  REDIRECT_TO_VIDEO_VISIT,
  REDIRECT_TO_WAITLIST,
  REDIRECT_TO_WAITLIST_ERROR,
  REFERRAL_CLICKED,
  REFERRAL_COVID_TEST,
  REFERRAL_UC,
  SMS_MOBILE_CHECK_IN_CLICK,
  SMS_PAYMENTS_REDIRECT_TO_INVOICE,
  SMS_REVIEW_CLICK,
  SMS_TWILIO_REDIRECT_TO_CONFIRMATION_PASS,
} from '../../core/analytics/events';
import { analyticsTrackEvent } from '../../core/analytics';
import {
  handleRedirectToReminders,
  handleRedirectToRemindersLastSms,
  handleRedirectToRemindersLastWebPush,
  handleRedirectToRemindersWebPush,
} from '../../components/Account/Profile/Reminders/util/redirects';
import { TrackingEntities, TrackingTypes, UTMMediumTypes } from '../../constants/dapi/tracking';
import { CONFIRMATION_EMAIL, POST_VISIT_SUMMARY } from '../../components/LoginForm/entryPoints';
import withAuthentication from '../../core/login/withAuthentication';
import { getNativeStoreDownloadLink } from '../../components/DownloadDriver/util';
import { isMobile } from '../../core/util/device';
import { convertSnakeToCamelCase, objectEntries, safeGet } from '../../core/util/object';
import { BASE_URL } from '../../config/index';
import { getStartingPointRouteForTelemedFlow } from '../../core/util/videoVisit';
import {
  fetchTranslateLocationIdNumericToIdHash,
  getLocationUrl,
  getSolvAndUclMergedLocationUrl,
} from '../../core/dapi/location';
import { hasQueryParams } from '~/core/util/url';
import { isPremiumBooking } from '~/core/util/booking';
import { RemixRoutes } from '~/core/remix';
import { COVID_SERVICE_IDS } from '~/constants/services';

const EMAIL = 'email';

type Props = {
  requestUrl: string;
  setInvoiceTrafficSource: (...args: any[]) => any;
  isAuthorized?: boolean;
};

type UtmCampaign = {
  utm_source: string;
  utm_campaign: string;
  utm_medium: string;
};

const sms_onboarding_campaign: UtmCampaign = {
  utm_source: 'onboarding-sms',
  utm_campaign: 'sms_account_activation',
  utm_medium: 'sms',
};

/**
 * Booking App Download Prompt Test Campaigns
 * https://solvhealth.atlassian.net/browse/MERRY-236
 *
 * We're dynamically injecting app downloads into three SMS communications for solv-originated
 * bookings:
 *    1. Booking Confirmation SMS
 *    2. Booking Reminder SMS
 *    3. Visit Check-In SMS
 *
 * If the user has already downloaded the app at the time of the SMS -- the download link will
 * not be included.
 */
const booking_app_download_prompt_confirmation_sms_campaign: UtmCampaign = {
  utm_campaign: 'booking-app-download-prompt',
  utm_source: 'booking-confirmation-sms',
  utm_medium: 'sms',
};

const booking_app_download_prompt_reminder_sms_campaign: UtmCampaign = {
  ...booking_app_download_prompt_confirmation_sms_campaign,
  utm_source: 'booking-reminder-sms',
};

const booking_app_download_prompt_check_in_sms_campaign: UtmCampaign = {
  ...booking_app_download_prompt_confirmation_sms_campaign,
  utm_source: 'visit-check-in-sms',
};

class Tracking extends Component<Props> {
  componentDidMount() {
    const { requestUrl } = this.props;
    const urlParts = requestUrl.split('/');
    const path = urlParts[0];
    const id = urlParts[1];

    switch (path) {
      // User clicks link to leave review on Google
      // eg. solv.me/t/g/{bookingId} redirects to Google page
      case 'gb':
      case 'g':
        this.handleGoogleRoute({ bookingId: id });
        break;

      // eg. solv.me/t/gl/{locationId} redirects to Google page
      case 'gl':
        this.handleGoogleRoute({ locationId: id });
        break;

      // redirects to Google's page, coming from email
      case 'ge':
        this.handleGoogleRouteEmail({ bookingId: id, source: EMAIL });
        break;

      case 's':
        this.handleReview(id);
        break;

      case 'sl':
        this.handleReview(id, requestUrl.split('/')[2]);
        break;

      case 'mc':
        this.handleMobileCheckIn(id);
        break;

      case 'mk':
        this.handleMobileCheckInRedirectToKiosk(id, MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_BROWSER);
        break;

      case 'mqr':
        this.handleMobileCheckInRedirectToKiosk(id, MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_QR_CODE);
        break;

      case 'mcb':
        this.handleMobileCheckInRedirectToWaitList(id);
        break;

      case 'tw':
        this.handleSmsRedirectToConfirmationPass(id);
        break;

      case 'p':
        this.handleSmsPayment(id);
        break;

      case 'pe':
        this.handleEmailPayment(id);
        break;

      case 'f':
        this.handleRedirectToBookingWidget(id);
        break;

      case 'w':
        this.handleRedirectToWaitlist(id);
        break;

      case 'c':
        /* for emails, if utm codes are already applied to the link, use 'bcc' instead to
         to maintain those query parameters. */
        this.handleRedirectToConfirmation(id, false);
        break;

      case 'bcc':
        this.handleRedirectToConfirmation(id, true);
        break;

      case 'cc':
        this.handleRedirectToAddCreditCard();
        break;

      case 'r':
        this.handleRedirectToReminder(id);
        break;

      case 'bcwl':
        this.handleRedirectToWaitListFromBookingConfirmationEmail(id);
        break;

      case 'bcpp':
        this.handleRedirectToProfilePageFromBookingConfirmationEmail(id);
        break;

      case 'bcwpl':
      case 'bcpl':
        /* walk-in and reg booking confirmations now being handled the same way in terms of tracking */
        this.handleRedirectToLoginFromBookingConfirmationEmail(id);
        break;

      case 'bcwwl':
        this.handleRedirectToWaitListFromBookingConfirmationWalkInEmail(id);
        break;

      case 'bcwpp':
        this.handleRedirectToProfilePageFromBookingConfirmationWalkInEmail(id);
        break;

      case 'vv':
        this.handleRedirectToVideoVideoFromConfirmationOrReminderTextMessage(id);
        break;

      case 'pr':
      case 'pc':
        /* for emails, if utm codes are already applied to the link, use 'bcp' instead to
 to maintain those query parameters. */
        this.handleRedirectToPaperwork(id, path);
        break;
      case 'bcp':
        this.handleRedirectToPaperwork(id, path, true);
        break;

      case 'bt':
        this.handleRedirectToCdpFromTransactionalOrWrapUpEmail(
          id,
          TRANSACTIONAL,
          BOOK_VISIT_FOOTER_TRANSACTIONAL
        );
        break;

      case 'bw':
        this.handleRedirectToCdpFromTransactionalOrWrapUpEmail(
          id,
          WRAPUP,
          BOOK_VISIT_FOOTER_WRAPUP
        );
        break;

      case 'bwr':
        this.handleRedirectToBookingWidgetFromTransactionalOrWrapUpEmail(
          id,
          WRAPUP,
          BOOK_VISIT_FOOTER_WRAPUP
        );
        break;

      case 'bwb':
        this.handleRedirectToRebookingPageFromWrapUpEmail(id);
        break;

      case 'pvs':
        this.handleRedirectToPostVisitSummary(id, 'sms');
        break;

      case 'pve':
        this.handleRedirectToPostVisitSummary(id, 'email');
        break;

      case 'ucr': {
        const timesSent = urlParts[2];
        handleRedirectToReminders(id, timesSent);
        break;
      }

      case 'ucrl':
        handleRedirectToRemindersLastSms(id);
        break;

      case 'ucrwp': {
        const timesSent = urlParts[2];
        handleRedirectToRemindersWebPush(id, timesSent);
        break;
      }

      case 'ucrlwp':
        handleRedirectToRemindersLastWebPush(id);
        break;

      case 'msg': {
        const medium = urlParts[2];
        this.handleRedirectToLoginFromChat(id, medium);
        break;
      }

      case 'pv': {
        const campaign_name = urlParts[0];
        const campaign_id = urlParts[1];
        const locationId = urlParts[2];
        this.handleRedirectToPatientRecallBookingWidget(locationId, campaign_name, campaign_id);
        break;
      }

      case 'ierc': {
        const medium = urlParts[2] === 'e' ? 'email' : 'push';
        const insuranceEligibilityChangeId = urlParts[3];
        const insuranceProfileId = urlParts[4];
        const campaign = urlParts[5];
        this.handleRedirectToAccountFromEligibility(
          id,
          medium,
          insuranceEligibilityChangeId,
          insuranceProfileId,
          campaign
        );
        break;
      }

      /**
       * ============================================
       * START: Booking App Download Prompt Campaigns
       * --------------------------------------------
       * @see Line 106 for explanation
       *
       */
      case 'bdacf': // download link in confirmation sms
        this.handleRedirectToNativeStore(
          booking_app_download_prompt_confirmation_sms_campaign.utm_campaign,
          booking_app_download_prompt_confirmation_sms_campaign
        );
        break;

      case 'bdar': // download link in reminder sms
        this.handleRedirectToNativeStore(
          booking_app_download_prompt_reminder_sms_campaign.utm_campaign,
          booking_app_download_prompt_reminder_sms_campaign
        );
        break;

      case 'bdaci': // download link in check-in sms
        this.handleRedirectToNativeStore(
          booking_app_download_prompt_check_in_sms_campaign.utm_campaign,
          booking_app_download_prompt_check_in_sms_campaign
        );
        break;

      /**
       * --------------------------------------------
       * END: Booking App Download Prompt Campaigns
       * ==========================================
       */

      case 'ntv': // Generic "Native" app download link that passes-through the campaign ID
        this.handleRedirectToNativeStore(id);
        break;

      case 'aasms': // "account activation" SMS -- when onboarding new users
        this.handleRedirectToNativeStore(
          sms_onboarding_campaign.utm_campaign,
          sms_onboarding_campaign
        );
        break;

      case 'lr':
        this.handleRedirectToLabResults(id, TrackingTypes.CLICK_SMS);
        break;

      case 'lre':
        this.handleRedirectToLabResults(id, TrackingTypes.CLICK_EMAIL);
        break;

      case 'lrs':
        this.handleRedirectToLabResults(id, TrackingTypes.CLICK_SMS);
        break;

      case 'lrp':
        this.handleRedirectToLabResults(id, TrackingTypes.CLICK_PUSH);
        break;

      case 'ref':
        if (id === 'uc') this.handleRedirectToUrgentCareSearch();
        else if (id === 'covidtest') this.handleRedirectToCovidTest();
        break;

      case 'rr':
        this.handleRedirectToReviewResponse(id);
        break;

      default:
        history.push('/');
        break;
    }
  }

  handleMobileCheckIn(bookingId: any) {
    const redirectToMobileCheckIn = () => history.replace(`/mobile-check-in/${bookingId}/start`);
    analyticsTrackEvent(SMS_MOBILE_CHECK_IN_CLICK, { bookingId });
    redirectToMobileCheckIn();
  }

  handleMobileCheckInRedirectToWaitList(bookingId: any) {
    const redirectToWaitList = () => history.replace(`/b/${bookingId}`);
    analyticsTrackEvent(MOBILE_CHECK_IN_REDIRECT_TO_WAITLIST, { bookingId });
    redirectToWaitList();
  }

  handleMobileCheckInRedirectToKiosk(numericLocationId: any, eventName: any) {
    fetchTranslateLocationIdNumericToIdHash(numericLocationId)
      .then((res: any) => {
        const locationIdHash = safeGet(res)('data.location_id_hash');
        if (!isEmptyString(locationIdHash)) {
          let url = `https://manage.solvhealth.com/welcome/${locationIdHash}`;
          if (eventName === MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_BROWSER) {
            url += `?mode=${CHECK_IN_TYPE_CONTACTLESS_BR}`;
          }
          if (eventName === MOBILE_CHECK_IN_REDIRECT_TO_KIOSK_FROM_QR_CODE) {
            url += `?mode=${CHECK_IN_TYPE_CONTACTLESS_QR}`;
          }
          const redirectKiosk = window.location.replace(url);
          analyticsTrackEvent(eventName, { locationId: locationIdHash });
          // @ts-expect-error ts-migrate(2349) FIXME: Type 'void' has no call signatures.
          redirectKiosk();
        }
      })
      .catch((e: any) => console.error(e));
  }

  handleSmsRedirectToConfirmationPass(bookingId: any) {
    const { isAuthorized } = this.props;

    analyticsTrackEvent(SMS_TWILIO_REDIRECT_TO_CONFIRMATION_PASS, {
      bookingId,
    });
    if (isAuthorized) {
      apiGetJson(`${getPublicBookingById(bookingId)}?fields=user_profile_id`)
        .then((r: any) => {
          history.replace(`${ACCOUNT_HOME_PATH}/${r.data.user_profile_id}/get-care`);
        })
        .catch((error: any) => history.replace('/'));
    } else {
      history.replace(`/pass/${bookingId}`);
    }
  }

  handleReview(bookingId: any, locationId = '') {
    const redirectToReview = () =>
      history.replace(`/add-review/${bookingId}?utm_source=discharge_review_text&utm_medium=sms`);
    if (!isEmptyString(locationId)) {
      analyticsTrackEvent(SMS_REVIEW_CLICK, { bookingId, locationId });
    } else {
      analyticsTrackEvent(SMS_REVIEW_CLICK, { bookingId });
    }

    apiGetJson(trackReviewLink(bookingId));
    redirectToReview();
  }

  handleGoogleRoute({ bookingId, locationId }: any = {}) {
    /**
     *
     */
    function redirectToGoogle(r: any) {
      window.location = r.data;
    }

    apiGetJson(getDapiGoogleReviewUrl({ bookingId, locationId }))
      .then(redirectToGoogle)
      .catch(() => history.push('/404'));
  }

  handleGoogleRouteEmail({ bookingId, source }: any = {}) {
    const redirectToReview = () => history.replace(`/add-review/${bookingId}`);

    apiGetJson(getDapiGoogleReviewEmailUrl({ bookingId, source }))
      .then(redirectToReview)
      .catch(() => history.push('/404'));
  }

  handleSmsPayment(invoiceId: any) {
    const redirectToPayments = () => history.replace(`/account/login/payments/${invoiceId}`);
    analyticsTrackEvent(SMS_PAYMENTS_REDIRECT_TO_INVOICE, { invoiceId });
    const url = getTrackingUrl(TrackingEntities.INVOICES, invoiceId, TrackingTypes.CLICK_SMS);
    apiPostJson(url, { type: TRACKING_PAYMENT_VIEW_INVOICE });
    this.props.setInvoiceTrafficSource('sms');
    redirectToPayments();
  }

  handleEmailPayment(invoiceId: any) {
    const redirectToPayments = () => history.replace(`/account/login/payments/${invoiceId}`);
    analyticsTrackEvent(EMAIL_PAYMENTS_REDIRECT_TO_INVOICE, { invoiceId });
    const url = getTrackingUrl(TrackingEntities.INVOICES, invoiceId, TrackingTypes.CLICK_EMAIL);
    apiPostJson(url, { type: TRACKING_PAYMENT_VIEW_INVOICE });
    this.props.setInvoiceTrafficSource('email');
    redirectToPayments();
  }

  handleRedirectToBookingWidget = async (bookingId: any) => {
    const response = await apiGetJson<{ location_id: string }>(getPublicBookingById(bookingId));
    const locationId = response.data.location_id;
    analyticsTrackEvent(REDIRECT_TO_BOOKING_WIDGET, { bookingId, locationId });
    apiPostJson(
      getTrackingUrl(TrackingEntities.BOOKINGS, bookingId, TrackingTypes.ACTION_REDIRECT),
      { type: TRACKING_CLICK_LINK_FOLLOW_UP_APPOINTMENT_TEXT }
    );
    history.replace(
      `/book-online/${locationId}?utm_source=follow_up_appointment_text&utm_medium=sms`
    );
  };

  handleRedirectToPatientRecallBookingWidget = (
    locationId: string,
    campaignName: string,
    campaignId: string
  ) => {
    const query = `?utm_source=patient_recall&utm_medium=${UTMMediumTypes.SMS}&utm_campaign=${campaignName}_${campaignId}`;
    window.location.replace(RemixRoutes.bookingWidget(locationId, query));
  };

  handleRedirectToWaitlist = (bookingId: any) => {
    apiGetJson(`${getPublicBookingById(bookingId)}?fields=status`)
      .then((r: any) => {
        const bookingStatus = r.data.status;
        analyticsTrackEvent(REDIRECT_TO_WAITLIST, { bookingId, bookingStatus });
        history.replace(`/b/${bookingId}?utm_source=waitlist_text&utm_medium=sms`);
      })
      .catch((error: any) => {
        analyticsTrackEvent(REDIRECT_TO_WAITLIST_ERROR, { bookingId, error });
        history.replace('/');
      });
  };

  handleRedirectToConfirmation = (bookingId: any, passThroughQueryParams: boolean = false) => {
    let queryParams = `?utm_source=confirmation_text&utm_medium=${UTMMediumTypes.SMS}`;

    if (passThroughQueryParams) {
      queryParams = hasQueryParams()
        ? `${window.location.search}`
        : `?utm_source=book_confirm_redirect&utm_medium=${UTMMediumTypes.EMAIL}`;
    }

    const { isAuthorized } = this.props;
    if (isAuthorized) {
      apiGetJson(getBookingById(bookingId))
        .then(({ data }: any) => {
          const booking = data;
          const options = {
            version: 'v4',
            fields: [
              'id',
              'is_paperwork_enabled',
              'is_payments_enabled',
              'is_standalone_telemed',
              'is_telemed',
            ],
          };
          apiGetJson(getLocationUrl(booking.location_id, options)).then(({ data }: any) => {
            const location = data;
            const accountUrl = `${ACCOUNT_HOME_PATH}/${booking.user_profile_id}/get-care${queryParams}`;
            let url = location.is_telemed
              ? getStartingPointRouteForTelemedFlow(booking, location)
              : accountUrl;
            if (isPremiumBooking(booking)) {
              url = `/pass/${bookingId}${queryParams}`;
            }
            history.replace(url);
          });
        })
        .catch((error: any) => {
          analyticsTrackEvent(REDIRECT_TO_CONFIRMATION_SHORTLINK_ERROR, {
            bookingId,
            error,
          });
          history.replace('/');
        });
    } else {
      history.replace(`/pass/${bookingId}${queryParams}`);
    }
  };

  handleRedirectToAddCreditCard = () => {
    analyticsTrackEvent(REDIRECT_TO_ADD_CREDIT_CARD_SHORT_LINK);
    history.replace('/payments/add-card/r/account/people');
  };

  handleRedirectToReminder = (bookingId: any) => {
    analyticsTrackEvent(REDIRECT_TO_REMINDER, { bookingId });
    history.replace(`/r/${bookingId}?utm_source=reminder_text&utm_medium=sms`);
  };

  handleRedirectToWaitListFromBookingConfirmationEmail = (bookingId: any) => {
    analyticsTrackEvent(BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_WAITLIST, {
      bookingId,
    });
    history.replace(
      `/b/${bookingId}?` +
        'utm_source=book_confirm&utm_campaign=book_confirm_march_2018&utm_medium=email'
    );
  };

  handleRedirectToProfilePageFromBookingConfirmationEmail = (userProfileId: any) => {
    analyticsTrackEvent(BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_PROFILE_PAGE, {
      userProfileId,
    });
    history.replace(
      `/account/people/${userProfileId}?` +
        'utm_source=book_confirm&utm_campaign=book_confirm_march_2018&utm_medium=email'
    );
  };

  handleRedirectToLoginFromBookingConfirmationEmail = (bookingId: any) => {
    let trackingProperties = { bookingId };

    const queryParams = hasQueryParams()
      ? `${window.location.search}`
      : `?utm_source=book_confirm_redirect&utm_medium=${UTMMediumTypes.EMAIL}`;

    apiGetJson(`${getPublicBookingById(bookingId)}?fields=first_login,user_profile_id`)
      .then((r: any) => {
        if (isEmptyString(r.data.first_login)) {
          const userProfileId = r.data.user_profile_id;

          analyticsTrackEvent(BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_LOGIN, {
            ...trackingProperties,
            userProfileId,
            firstLogin: true,
          });
          history.replace(`/account/login/account/welcome/${userProfileId}${queryParams}`);
        } else {
          analyticsTrackEvent(BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_LOGIN, {
            ...trackingProperties,
            firstLogin: false,
          });
          history.replace(`/account/login${queryParams}`);
        }
      })
      .catch((error: any) => {
        analyticsTrackEvent(BOOKING_CONFIRMATION_EMAIL_REDIRECT_TO_LOGIN_ERROR, {
          ...trackingProperties,
          error,
        });
        history.replace(`/account/login?ep=${CONFIRMATION_EMAIL}&utm_source=error`);
      });
  };

  handleRedirectToWaitListFromBookingConfirmationWalkInEmail = (bookingId: any) => {
    analyticsTrackEvent(BOOKING_CONFIRMATION_WALK_IN_EMAIL_REDIRECT_TO_WAITLIST, { bookingId });
    history.replace(
      `/b/${bookingId}?` +
        'utm_source=c_book_confirm_walkin&utm_medium=email&utm_campaign=book_confirm_walkin_nov_2018'
    );
  };

  handleRedirectToProfilePageFromBookingConfirmationWalkInEmail = (userProfileId: any) => {
    analyticsTrackEvent(BOOKING_CONFIRMATION_WALK_IN_EMAIL_REDIRECT_TO_PROFILE_PAGE, {
      userProfileId,
    });
    history.replace(
      `/account/people/${userProfileId}?` +
        'utm_source=c_book_confirm_walkin&utm_medium=email&utm_campaign=book_confirm_walkin_nov_2018'
    );
  };

  handleRedirectToVideoVideoFromConfirmationOrReminderTextMessage = (bookingId: any) => {
    apiGetJson(`${getPublicBookingById(bookingId)}`)
      .then((r: any) => {
        const booking = convertSnakeToCamelCase(r.data);
        const { locationId } = booking;
        apiGetJson(`${getSolvAndUclMergedLocationUrl(locationId)}`)
          .then((r: any) => {
            const location = convertSnakeToCamelCase(r.data);
            analyticsTrackEvent(REDIRECT_TO_VIDEO_VISIT, { bookingId });
            history.replace(getStartingPointRouteForTelemedFlow(booking, location));
          })
          .catch((error: any) => {
            console.error(
              'handleRedirectToVideoVideoFromConfirmationOrReminderTextMessage error',
              error
            );
            history.push(`/pass/${bookingId}`);
          });
      })
      .catch((error: any) => {
        console.error(
          'handleRedirectToVideoVideoFromConfirmationOrReminderTextMessage error',
          error
        );
        history.push(`/pass/${bookingId}`);
      });
  };

  handleRedirectToPaperwork = (
    bookingId: any,
    path: any,
    passThroughQueryParams: boolean = false
  ) => {
    let utmSource;
    let utmMedium;

    if (path === PAPERWORK_TEXT_UPON_RESERVED) {
      utmSource = 'c_paperwork_text_upon_reserved';
      utmMedium = UTMMediumTypes.SMS;
    } else if (path === PAPERWORK_TEXT_UPON_CONFIRMED) {
      utmSource = 'c_paperwork_text_upon_confirmed';
      utmMedium = UTMMediumTypes.SMS;
    }

    if (passThroughQueryParams) {
      const queryParams = hasQueryParams()
        ? `${window.location.search}`
        : `?utm_source=book_confirm_redirect&utm_medium=${UTMMediumTypes.EMAIL}`;

      analyticsTrackEvent(REDIRECT_TO_PAPERWORK, { bookingId });
      history.replace(`/paperwork/start/${bookingId}${queryParams}`);
    } else {
      analyticsTrackEvent(REDIRECT_TO_PAPERWORK, { bookingId, utmSource, utmMedium });
      history.replace(
        `/paperwork/start/${bookingId}?utm_source=${utmSource}&utm_medium=${utmMedium}`
      );
    }
  };

  handleRedirectToCdpFromTransactionalOrWrapUpEmail = (
    bookingId: any,
    emailType: any,
    eventName: any
  ) => {
    apiPostJson(getTrackingUrl('bookings', bookingId, 'action.redirect'), {
      type: eventName,
      emailType,
    });
    apiGetJson(`${getPublicBookingById(bookingId)}?fields=location_id`)
      .then((r: any) => {
        history.replace(`/-${r.data.location_id}?utm_source=${emailType}&utm_medium=email`);
      })
      .catch((error: any) => {
        analyticsTrackEvent(REDIRECT_TO_CDP_ERROR, { bookingId, error });
        history.replace('/');
      });
  };

  handleRedirectToBookingWidgetFromTransactionalOrWrapUpEmail = (
    bookingId: any,
    emailType: any,
    eventName: any
  ) => {
    apiPostJson(getTrackingUrl('bookings', bookingId, 'action.redirect'), {
      type: eventName,
      emailType,
    });
    apiGetJson(`${getPublicBookingById(bookingId)}?fields=location_id`)
      .then((r: any) => {
        history.replace(
          `/book-online/${r.data.location_id}?utm_source=${emailType}&utm_medium=email`
        );
      })
      .catch((error: any) => {
        analyticsTrackEvent(REDIRECT_TO_BOOKING_WIDGET_ERROR, { bookingId, error });
        history.replace('/');
      });
  };

  handleRedirectToLoginFromChat = (messageId: any, medium: any) => {
    analyticsTrackEvent(CHAT_UNREAD_MESSAGE_REDIRECT, { medium });
    history.replace('/account/login?open_chat=true&skip_onboarding=true');
  };

  handleRedirectToRebookingPageFromWrapUpEmail = (bookingId: any) => {
    const { isAuthorized } = this.props;
    apiPostJson(getTrackingUrl('bookings', bookingId, 'action.redirect'), {
      type: BOOK_VISIT_BODY_WRAPUP,
    });
    if (isAuthorized) {
      apiGetJson(`${getPublicBookingById(bookingId)}?fields=user_profile_id`)
        .then((r: any) => {
          history.replace(
            `${ACCOUNT_HOME_PATH}/${r.data.user_profile_id}/get-care?utm_source=wrapup&utm_medium=email`
          );
        })
        .catch((error: any) => history.replace('/'));
    } else {
      history.replace(`/pass/${bookingId}?utm_source=wrapup&utm_medium=email`);
    }
  };

  handleRedirectToPostVisitSummary = (bookingId: any, medium: any) => {
    apiPostJson(getTrackingUrl('bookings', bookingId, 'action.redirect'), {
      type: POST_VISIT_SUMMARY_CLICK_NOTIFICATION,
    });
    apiGetJson(`${getPublicBookingById(bookingId)}?fields=user_profile_id`)
      .then((r: any) => {
        history.replace(
          `${ACCOUNT_HOME_PATH}/${r.data.user_profile_id}/manage?b=${bookingId}` +
            `&utm_source=consumer&utm_medium=${medium}&utm_campaign=post_visit_summary?ep=${POST_VISIT_SUMMARY}`
        );
      })
      .catch((error: any) => {
        analyticsTrackEvent(REDIRECT_TO_PVS_ERROR, { bookingId, error });
        history.replace(`${ACCOUNT_HOME_PATH}?ep=${POST_VISIT_SUMMARY}`);
      });
  };

  handleRedirectToAccountFromEligibility = (
    userProfileId: any,
    medium: any,
    insuranceEligibilityId: any,
    insuranceProfileId: any,
    campaign: any
  ) => {
    const trackingType = medium === 'email' ? TrackingTypes.CLICK_EMAIL : TrackingTypes.CLICK_PUSH;
    apiPostJson(
      getTrackingUrl(TrackingEntities.INSURANCE_PROFILES, insuranceProfileId, trackingType),
      {
        insurance_eligibility_changes_id: insuranceEligibilityId,
        user_profile_id: userProfileId,
        medium,
        campaign,
      }
    );
    history.replace(
      `/account/people/${userProfileId}/benefits?utm_campaign=${campaign}&utm_source=consumer&utm_medium=${medium}`
    );
  };

  handleRedirectToNativeStore = (
    campaignName: any,
    customUrlCampaigns: UtmCampaign | null = null
  ) => {
    analyticsTrackEvent(DOWNLOAD_DRIVER_CLICK_LINK, { campaignName, ...customUrlCampaigns });
    let nativeStoreLink;

    const urlCampaigns =
      typeof window !== 'undefined' &&
      window.location.search
        .replace('?', '')
        .split('&')
        .reduce((acc, s) => {
          const keyValue = s.split('=');
          return Object.assign(acc, { [keyValue[0].toLowerCase()]: keyValue[1] });
        }, {});

    if (!isMobile()) {
      nativeStoreLink = `${BASE_URL}/download?${objectEntries(customUrlCampaigns || urlCampaigns)
        .map(([k, v]) => `${k}=${v}`)
        .join('&')}`;
    } else {
      nativeStoreLink = getNativeStoreDownloadLink(
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'utm_campaign' does not exist on type 'fa... Remove this comment to see the full error message
        urlCampaigns.utm_campaign || campaignName || 'generic-download-driver'
      );
      if (nativeStoreLink.includes('referrer')) {
        nativeStoreLink = `${nativeStoreLink.split('&referrer')[0]}&referrer=${objectEntries(
          customUrlCampaigns || urlCampaigns
        )
          .map(([k, v]) => `${k}%3D${v}`)
          .join('%26')}%26anid%3Dadmob`;
      }
    }

    window.open(nativeStoreLink, '_self');
  };

  handleRedirectToLabResults = (labResultId: string, trackingType: string) => {
    apiPostJson(getTrackingUrl(TrackingEntities.LAB_RESULTS, labResultId, trackingType), {
      viewing_lab_result: labResultId,
    });

    history.replace(`/lab-results/${labResultId}?skip_onboarding=true`);
  };

  handleRedirectToUrgentCareSearch = () => {
    analyticsTrackEvent(REFERRAL_CLICKED, { type: REFERRAL_UC });
    window.location.href = RemixRoutes.search.base;
  };

  handleRedirectToCovidTest = () => {
    analyticsTrackEvent(REFERRAL_CLICKED, { type: REFERRAL_COVID_TEST });
    window.location.href = RemixRoutes.search.controlled({
      query: 'COVID test',
      covidRelated: true,
      serviceIds: COVID_SERVICE_IDS.join(','),
    });
  };

  handleRedirectToReviewResponse = (reviewId: string) => {
    analyticsTrackEvent(REDIRECT_TO_REVIEW_RESPONSE);
    window.location.href = RemixRoutes.reviewResponse(reviewId);
  };

  render() {
    return <div />;
  }
}

export default withAuthentication(Tracking);
